/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react';
import { ITag } from '@fluentui/react/lib/Pickers';
import { getId } from '@fluentui/react/lib/Utilities';
import differenceWith from 'lodash.differencewith';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Pill } from 'src/core/components/Pill';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TagContainer } from 'src/core/components/TagContainer';
import { TagInputField } from 'src/core/components/TagInputField';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';

export type SublistSynonymsCellProps = {
    values: string[];
    isEditMode: boolean;
    onAddValues: (values: string[]) => void;
    onRemoveValues: (values: string[]) => void;
    isRemovingDisabled?: boolean;
    onExitFocus?: () => void;
};

/**
 * Renders the cell that displays and controls the synonyms
 * belonging to the current list entity opened in the entity
 * details segment.
 */
export const SublistSynonymsCell = observer((props: SublistSynonymsCellProps) => {
    const { values, isEditMode, onAddValues, onRemoveValues, isRemovingDisabled, onExitFocus } = props;

    const { rootStore } = useGlobalState();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const tags: ITag[] = values.map(v => ({ key: getId('SublistSynonym'), name: v }));
    const tagItems = tags.map((t, index) => (
        <RBACTooltip
            displayedUserRole={displayedUserRole}
            hidden={canWrite || isRemovingDisabled}
            data-automation-id="removeSynonymsButtonLanguageToolTip"
            componentSurroundedByToolTip={
                <Pill
                    name={t.name}
                    index={index}
                    pillKey={t.key}
                    onRemovePill={() => onRemoveValues([t.name])}
                    isDeleteButtonDisabled={!canWrite || isRemovingDisabled}
                />
            }
        />
    ));

    /**
     * Signals for the given tags to be removed.
     *
     * @param tagsToRemove The tags to remove.
     */
    const onTagContainerRemoveValues = (tagsToRemove: ITag[]): void => {
        onRemoveValues(tagsToRemove.map(t => t.name));
    };

    /**
     * Signals the last value in the values array for removal.
     */
    const popLastValue = (): void => {
        const lastValue = values[values.length - 1];

        onRemoveValues([lastValue]);
    };

    /**
     * Ensures that no duplicity of values occur when
     * adding new sublist values.
     *
     * @param newValues The new values to add.
     */
    const filterAndAddValues = (newValues: string[]) => {
        const filteredValues = differenceWith(newValues, values);

        if (filteredValues.length === 0) {
            return;
        }

        onAddValues(filteredValues);
    };

    return (
        <TagContainer<ITag> onRemoveItems={onTagContainerRemoveValues} onEscPress={onExitFocus}>
            <Stack horizontal wrap role="list">
                {tagItems}
            </Stack>
            {isEditMode ? (
                <TagInputField
                    submitChars={['Enter', ',']}
                    onSubmit={filterAndAddValues}
                    onEmptyFieldBackspace={popLastValue}
                    autoFocus={false}
                />
            ) : null}
        </TagContainer>
    );
});
